import { AxiosError } from 'axios';
import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_URLS, ERROR_MESSAGES } from '../../const/AppConst';
import { IApiErrors, IDocumentType, IEventRequest, IdentityType } from '../../context/model';
import MgpMessage from '../../elements/MgpMessage/MgpMessage';
import LoaderModal from '../../modals/common/LoaderModal';
import UploadFilePreviewModal from '../../modals/common/UploadFilePreviewModal';
import { replaceDocument, uploadFamilyDocument, uploadIdentityDocument, uploadRib, uploadSocialSecurity } from '../../services/CustomerApiService';
import { pushEventMessage } from '../../services/NotificationService';
import { extractJwtToken, getCompressFile, isAcceptedFileSize, isValidFileSize, millisToMinutesAndSeconds, isValidFileType } from '../../utils/utils';
import MgpInputFileUpload from '../../elements/MgpInputFileUpload/MgpInputFileUpload';
import UploadDocumentButton from '../common/UploadDocumentButton';
import UploadMultipleComponent from '../common/UploadMultipleComponent';
import UploadTwoSidedMobileComponent from '../common/UploadTwoSidedMobileComponent';



type UploadPropositionDocComponentProps = {
  token: string;
  propositionId: string;
  docType: IDocumentType;
  eventId: string;
  docId?: string
}


export default function UploadPropositionDocComponent({token, propositionId, docType, eventId, docId} : UploadPropositionDocComponentProps) {

  const [errorMsg, setErrorMsg] = useState<string | null>();
  const [loading, setLoading] = useState<boolean>(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [errorFileTypeMsg, setErrorFileTypeMsg] = useState<boolean | null>();
  const [errorFileSizeMsg, setErrorFileSizeMsg] = useState<boolean | null>();
  const [showUploadMultiple, setShowUploadMultiple] = useState<boolean>(false);
  const [hasMultipleFile, setHasMultipleFile] = useState<boolean>(false);
  const [timer, setTimer ] = useState<number>(600000);
  const [showUploadTwoSide, setShowUploadTwoSide] = useState<boolean>(false);

  useEffect(() => {
    const eventBackMsg = {eventId: eventId + "_open", message: "Open"} as IEventRequest;
    pushEventMessage(token, eventBackMsg);

    let interval = setInterval(() => {
      setTimer((prevTime) => prevTime - 1000);
    }, 1000);

    setTimeout(() => {
      clearInterval(interval);
      setTimer(0);
    }, 600000);

    return () => clearInterval(interval);
  },[]);

  const extractedJwt = extractJwtToken(token);
 

  const navigate = useNavigate();

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>, name: string) => {
    try {
      const uploadedfile = event.target.files ? event.target.files[0] : null;
      setUploadedFile(uploadedfile);
      if (!isValidFileType(uploadedfile)) {
        setErrorFileTypeMsg(true)
      } else if (!isValidFileSize(uploadedfile)) {
        setErrorFileSizeMsg(true)
      } else if (!isAcceptedFileSize(uploadedfile)) {
        try {
          const token = extractedJwt!.accessToken;
          const newFile = await getCompressFile(token, uploadedfile as File);
          setUploadedFile(newFile);
          setModalIsOpen(true);
        } catch(e) {
          setErrorMsg("Suite à un incident technique, nous ne pouvons pas uploader votre fichier. Veuillez sélectionner à nouveau.");
        }
      } else {
        setModalIsOpen(true);
      }
    } catch (error) {
      const err = error as AxiosError;
      console.log(err);
      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else {
        setErrorMsg("Une erreur technique est survenue ");
      }
    }  
    
  };
 
  const documentSubmit = async (fileUpload: File, backSideFile? : File) => {
    setErrorMsg(null);
    setErrorFileTypeMsg(false);
    setErrorFileSizeMsg(false);
    setLoading(true);

    try {
      let response = null; 
      if (docId) {
        response = await replaceDocument(token, propositionId,docId, fileUpload, true, eventId);
      } else {
        if(docType.code === 'SOCIAL_SECURITY_CERTIFICATE') {
          response = await uploadSocialSecurity(token, propositionId, fileUpload, true, true, true, eventId);
        } else if(docType.code === 'SOCIAL_SECURITY_CERTIFICATE_BENEFICIARY') {
          response = await uploadSocialSecurity(token, propositionId, fileUpload, true, false, true, eventId);
        } else if(docType.code === 'RIB') {
          response = await uploadRib(token, propositionId, fileUpload, true, true, eventId);
        } else if(docType.category === 'identity') {
          response = await uploadIdentityDocument(token, propositionId, fileUpload, backSideFile ?? null, docType.code, true, true, eventId);
        } else if(docType.category === 'family') {
          response = await uploadFamilyDocument(token, propositionId, fileUpload, docType.code, true, eventId);
        }
     }

     setLoading(false);
      if (response && response.data) {
        navigate({
          pathname: APP_URLS.uploadMobileConfirmPage,
          search: `?id=${propositionId}`,
        }, { replace: true });
      } else {
          setErrorMsg(ERROR_MESSAGES.lowQualityDoc);
      }
    } catch (error) {
      setLoading(false);
      setShowUploadMultiple(false);
      setShowUploadTwoSide(false);
      setModalIsOpen(false);

      const err = error as AxiosError;
      console.log(err);
      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        if (errorData.code === "DOCUMENT_TYPE_ERROR") {
          setErrorMsg("Le document fourni ne correspond pas au type sélectionné.");
        } else if (errorData.code === "DOC_VALIDITY_ERROR") {
          setErrorMsg("Votre pièce d'identité n'a pu être validée par notre système. Une pièce lisible et en cours de validité est nécessaire.")
        } else if (errorData.code === "OCR_BIRTHDATE_ERROR") {
          setErrorMsg("Les données du souscripteur ne correspondent pas à la pièce fournie.")
        } else {
          setErrorMsg("Erreur: " + errorData.message);
        }  
      } else {
        setErrorMsg("Une erreur technique est survenue ");
      }
      
    }
  }

  if (timer === 0) {
    return <MgpMessage type="danger" message="Le lien n'est plus valid. Veuillez retourner sur votre navigateur principal." hasCloseBtn={false}/>
  } 

  let uploadButton;
  if (docType?.category === "family") {
      uploadButton = <UploadDocumentButton onClick={() => setShowUploadMultiple(true)} />;
  } else if (docType?.code === IdentityType.CNI || docType?.code === IdentityType.CST) {
      uploadButton = <UploadDocumentButton onClick={() => setShowUploadTwoSide(true)} />;
  } else {
      uploadButton = (
          <MgpInputFileUpload
              id={docType?.code || 'attach'}
              onChange={(e) => handleFileChange(e, docType?.code)}
              onClick={(e) => e.currentTarget.value = ''}
          />
      );
  }

  return (
    <form style={{width: '100%'}}>    
        <div>
          {
            errorFileTypeMsg &&
            <div className='form-row'>
              <div className='form-group mw-70'>
                <p className='text blanc p-sm bg-fushia'>{ERROR_MESSAGES.fileTypeMsg}</p> 
              </div>   
            </div>
          }
          {
            errorFileSizeMsg &&
            <div className='form-row'>
              <div className='form-group mw-70'>
                <p className='text blanc p-sm bg-fushia'>{ERROR_MESSAGES.fileSizeMsg}</p> 
              </div> 
            </div>
          }
         
          <p className="text mb-sm">Veuillez télécharger votre {docType ? docType.label.toLowerCase() : 'document'}. <span className='bleu fw-medium'> Ce lien va expirer dans {millisToMinutesAndSeconds(timer)}.</span></p>
          <div className="form-row">
            <div className="form-group mw-30">
              
              {uploadButton}
           
          </div>
        </div>
      </div>
      {modalIsOpen && uploadedFile &&<UploadFilePreviewModal file={uploadedFile} onUploadAction={() => documentSubmit(uploadedFile)} isOpen={modalIsOpen} onCloseModal={closeModal}/>}

      {showUploadMultiple && <UploadMultipleComponent isOpen={showUploadMultiple} onCloseModal={() => setShowUploadMultiple(false)} docType={docType?.code} onUploadAction={documentSubmit} setHasMultipleFile={(response : boolean) => setHasMultipleFile(response) } accessToken={extractedJwt!.accessToken}/>}
      {showUploadTwoSide && <UploadTwoSidedMobileComponent isOpen={showUploadTwoSide} onCloseModal={() => setShowUploadTwoSide(false)} docType={docType?.code} onUploadAction={documentSubmit} />}
      { loading && <LoaderModal hasMessage={false}/>}
       
      {errorMsg && <MgpMessage type="danger" message={errorMsg} hasCloseBtn={true}/>}
       
      </form>
  )
}
